import * as dat from 'dat.gui';
import GSAP from 'gsap';

const TOTAL_DURATION_MS = 20 * 1000;
const DRIFT_DURATION_S = 0.5;
const DRIFT_EASE = 'power2.out';
const ENTRANCE_THRESHOLD_PX = 0.5;
const ENTRANCE_DURATION_S = 0.2;

function loadAnimations() {
	const ctx = require.context('../../shared/animation', false, /\.json$/);
	const seen = new Set();
	return ctx
		.keys()
		.map((k) => {
			const name = k
				.split('/')
				.pop()
				.replace(/\.json$/, '');
			return { name, data: ctx(k) };
		})
		.filter((a) => {
			if (seen.has(a.name)) return false;
			if (
				!a.data ||
				!Array.isArray(a.data.samples) ||
				a.data.samples.length === 0
			)
				return false;
			seen.add(a.name);
			return true;
		});
}

function shuffle(arr) {
	const a = arr.slice();
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

function distributeSlack(slackMs, slots) {
	if (slackMs <= 0 || slots <= 0) return new Array(Math.max(slots, 0)).fill(0);
	const weights = new Array(slots).fill(0).map(() => Math.random());
	const sum = weights.reduce((a, b) => a + b, 0) || 1;
	return weights.map((w) => (w / sum) * slackMs);
}

export default class EyePlayer {
	constructor({ aboutSection }) {
		this.about = aboutSection;
		this.debug = window.location.hash === '#debug';

		this.animations = loadAnimations();
		this.started = false;
		this.timeline = null;

		this.monitors = {
			state: 'idle',
			loaded: this.animations.length,
		};

		console.log(
			`[EyePlayer] Loaded ${this.animations.length} animation(s):`,
			this.animations.map((a) => a.name)
		);

		if (!this.animations.length) {
			console.warn(
				'[EyePlayer] No animations found in shared/animation/. Drop .json files there and rebuild.'
			);
		}

		if (this.debug) this._createGui();
	}

	update() {
		if (this.debug) return;
		if (!this.about?.isMobile?.matches) return; // desktop: instant user control, no sequence
		if (!this.started && this.about?.inView && this.animations.length) {
			this.start();
		}
	}

	_createGui() {
		this.gui = new dat.GUI({ width: 280 });
		const folder = this.gui.addFolder('Eye Player');
		folder.add(this.monitors, 'state').listen();
		folder.add(this.monitors, 'loaded').listen();

		const seq = folder.addFolder('Sequence (90s)');
		const seqActions = {
			start: () => this.start(),
			stop: () => this.cancel(),
		};
		seq.add(seqActions, 'start').name('Start sequence');
		seq.add(seqActions, 'stop').name('Stop');
		seq.open();

		const list = folder.addFolder(`Animations (${this.animations.length})`);
		this.animations.forEach((anim) => {
			const dur = (anim.data.duration / 1000).toFixed(2);
			const obj = { play: () => this.playSingle(anim) };
			list.add(obj, 'play').name(`▶ ${anim.name} (${dur}s)`);
		});
		list.open();

		folder.open();
	}

	start() {
		this.cancel();
		this.started = true;

		const eyes = this._eyes();
		if (!eyes.length || !this.animations.length) return;

		const order = shuffle(this.animations);
		const sumDurMs = order.reduce((s, a) => s + (a.data.duration || 0), 0);
		const slackMs = Math.max(0, TOTAL_DURATION_MS - sumDurMs);
		const gaps = distributeSlack(slackMs, order.length + 1);

		this.about.playback = true;
		this.monitors.state = 'sequence';
		console.log(
			`[EyePlayer] Sequence start — order: ${order
				.map((a) => a.name)
				.join(', ')}, slack: ${(slackMs / 1000).toFixed(1)}s`
		);

		const tl = GSAP.timeline({ repeat: -1 }); // phone: loop forever, never hand control back
		tl.set(eyes, { x: 0, y: 0 });

		for (let i = 0; i < order.length; i++) {
			this._appendGap(tl, eyes, gaps[i] / 1000);
			this._appendAnimation(tl, eyes, order[i].data);
		}
		this._appendGap(tl, eyes, gaps[order.length] / 1000);

		this.timeline = tl;
	}

	playSingle(anim) {
		this.cancel();

		const eyes = this._eyes();
		if (!eyes.length) return;

		this.about.playback = true;
		this.monitors.state = `playing: ${anim.name}`;
		console.log(`[EyePlayer] Single play: ${anim.name}`);

		const tl = GSAP.timeline({ onComplete: () => this._onFinish() });
		tl.to(eyes, { x: 0, y: 0, duration: 0.3, ease: DRIFT_EASE });
		this._appendAnimation(tl, eyes, anim.data);
		this.timeline = tl;
	}

	cancel() {
		if (this.timeline) {
			this.timeline.kill();
			this.timeline = null;
		}
		if (this.about) this.about.playback = false;
		this.monitors.state = 'idle';
	}

	_eyes() {
		return [
			this.about?.elements?.eyeLeft,
			this.about?.elements?.eyeRight,
		].filter(Boolean);
	}

	_appendGap(tl, eyes, gapSeconds) {
		if (gapSeconds <= 0) return;
		const driftS = Math.min(DRIFT_DURATION_S, gapSeconds);
		tl.to(eyes, { x: 0, y: 0, duration: driftS, ease: DRIFT_EASE });
		const holdS = gapSeconds - driftS;
		if (holdS > 0) tl.to({}, { duration: holdS });
	}

	_appendAnimation(tl, eyes, data) {
		const samples = data.samples;
		const first = samples[0];
		if (
			Math.abs(first.x) > ENTRANCE_THRESHOLD_PX ||
			Math.abs(first.y) > ENTRANCE_THRESHOLD_PX
		) {
			tl.to(eyes, {
				x: first.x,
				y: first.y,
				duration: ENTRANCE_DURATION_S,
				ease: 'power1.out',
			});
		}
		for (let i = 1; i < samples.length; i++) {
			const s = samples[i];
			const prev = samples[i - 1];
			const dt = Math.max(0.001, (s.t - prev.t) / 1000);
			tl.to(eyes, { x: s.x, y: s.y, duration: dt, ease: 'none' });
		}
	}

	_onFinish() {
		this.timeline = null;
		this.monitors.state = 'done';
		if (!this.about) return;
		this.about.x = 0;
		this.about.y = 0;
		this.about.targetX = 0;
		this.about.targetY = 0;
		this.about.playback = false;
	}

	destroy() {
		this.cancel();
		this.gui?.destroy();
	}
}
